'use client';

import { motion } from 'framer-motion';
import { BRAND_COLORS } from '@/constants';
import { useProgramsData } from '@/lib/hooks/usePrograms';
import { mapProgramsFromBackend } from '@/lib/mappers/programsMapper';
import { OurProgramsLoadingSkeleton } from '@/components/ui/OurProgramsLoadingSkeleton';

const OurProgramsSectionClient = ({ dict }: { dict: any }) => {
  console.log('🔍 [Programs Client] Component rendering...');
  
  const { data: apiData, loading, error } = useProgramsData();
  
  console.log('🔍 [Programs Client] Hook state:', {
    hasData: !!apiData,
    loading,
    error: error?.message || null,
    dataSuccess: apiData?.success || null,
    dataLength: apiData?.data?.length || 0
  });

  const model = apiData ? mapProgramsFromBackend(apiData, dict) : null;
  
  console.log('🔍 [Programs Client] Model state:', {
    hasModel: !!model,
    title: model?.title || null,
    programsCount: model?.programs?.length || 0,
    programs: model?.programs?.map(p => ({ title: p.title, imageSrc: p.image.src })) || []
  });

  if (loading) {
    return <OurProgramsLoadingSkeleton />;
  }

  if (error) {
    console.error('Programs data loading failed:', error);
    return null;
  }

  if (!model) {
    return null;
  }

  return (
    <section 
      className="py-20" 
      style={{ backgroundColor: model.styling.backgroundColor }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.p
            className="font-semibold uppercase tracking-wide mb-2"
            style={{ color: BRAND_COLORS.PRIMARY_BLUE, fontSize: '15px' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            {model.badge}
          </motion.p>
          <motion.h2
            className="font-extrabold programs-title-dark"
            style={{ fontSize: '37px' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            {model.title}
          </motion.h2>
        </div>

        {/* Programs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {model.programs.map((program, index) => (
            <motion.div 
              key={program.id} 
              className="group relative rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{ y: -5 }}
            >
              {/* Program Image */}
              <div className="aspect-[4/3] relative overflow-hidden">
                <img
                  src={program.image.src}
                  alt={program.image.alt}
                  className="w-full h-full object-cover"
                  onLoad={() => {
                    console.log('✅ [Programs Client] Image loaded successfully:', program.image.src);
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    console.error('❌ [Programs Client] Image failed to load:', {
                      src: program.image.src,
                      alt: program.image.alt,
                      error: e
                    });
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) {
                      fallback.style.display = 'flex';
                      console.log('🔍 [Programs Client] Showing fallback for:', program.title);
                    }
                  }}
                />
                {/* Fallback content when image fails to load */}
                <div 
                  className="absolute inset-0 flex items-center justify-center hidden"
                  style={{ 
                    background: model.layout.showGradient 
                      ? `linear-gradient(135deg, ${model.styling.gradientFrom} 0%, ${model.styling.gradientTo} 100%)`
                      : model.styling.cardBackgroundColor
                  }}
                >
                  <div className="text-center" style={{ color: model.styling.titleColor }}>
                    <svg className="w-20 h-20 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <p className="text-sm font-medium">{program.image.placeholder}</p>
                  </div>
                </div>
              </div>

              {/* Overlay with Program Title */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end">
                <div className="p-6 w-full">
                  <h3 
                    className="text-xl font-bold mb-2 text-white"
                    style={{ color: '#FFFFFF !important' }}
                  >
                    {program.title}
                  </h3>
                </div>
              </div>

              {/* Hover Content */}
              {model.layout.showHoverEffect && (
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center p-6"
                  style={{ backgroundColor: model.styling.overlayColor }}
                >
                  <div className="text-center text-white">
                    <p 
                      className="text-base mb-4 font-semibold"
                      style={{ color: '#FFFFFF !important', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
                    >
                      {program.description}
                    </p>
                    <button 
                      className="px-6 py-2 rounded-full font-medium transition-all duration-200 hover:scale-105"
                      style={{ 
                        backgroundColor: model.styling.buttonBackgroundColor,
                        color: model.styling.buttonTextColor,
                        border: `1px solid ${model.styling.buttonBackgroundColor}`
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = model.styling.buttonHoverBackgroundColor;
                        e.currentTarget.style.color = model.styling.buttonHoverTextColor;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = model.styling.buttonBackgroundColor;
                        e.currentTarget.style.color = model.styling.buttonTextColor;
                      }}
                    >
                      {model.cta.text}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OurProgramsSectionClient;
